import { useState } from 'react';
import {
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  RadioGroup,
  Radio,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  List,
  ListItem,
  ListIcon,
  Badge,
  Box,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';
import { useAppSelector, useAppDispatch } from 'hooks';
import {
  setAllDocumentsAttached,
  useLegacyAutoFormLocked,
} from 'reducers/legacyAutoFormReducer';

const DocumentComplianceSection = () => {
  const dispatch = useAppDispatch();
  const isLocked = useLegacyAutoFormLocked();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const allDocumentsAttached = useAppSelector(
    (state) => state.legacyAutoForm.data.additional_info.all_documents_attached,
  );
  const validationErrors = useAppSelector(
    (state) => state.legacyAutoForm.validationErrors,
  );

  const handleChange = (value: string) => {
    if (value === 'yes') {
      setIsModalOpen(true);
    } else {
      dispatch(setAllDocumentsAttached(false));
    }
  };

  const handleConfirm = () => {
    dispatch(setAllDocumentsAttached(true));
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Card mb="20px" id="sectionDocumentCompliance">
        <Text fontWeight="bold" fontSize="2xl" mb="20px">
          Document & Abeyance Compliance
        </Text>

        <VStack spacing={4} align="stretch">
          <FormControl
            isRequired
            isInvalid={!!validationErrors?.all_documents_attached}
          >
            <FormLabel ml="0">
              Are All Required Documents Attached And Abeyances Set?
            </FormLabel>
            <RadioGroup
              value={allDocumentsAttached ? 'yes' : 'no'}
              onChange={handleChange}
              isDisabled={isLocked}
            >
              <HStack spacing={8}>
                <Radio value="yes">Yes</Radio>
                <Radio value="no">No</Radio>
              </HStack>
            </RadioGroup>
            {validationErrors?.all_documents_attached && (
              <FormErrorMessage>
                {validationErrors.all_documents_attached}
              </FormErrorMessage>
            )}
          </FormControl>
        </VStack>
      </Card>

      <Modal isOpen={isModalOpen} onClose={handleCancel} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Document Compliance</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4} color="gray.600">
              Please ensure the following documents are attached to the client's
              PowerBroker record:
            </Text>

            <Box mb={4}>
              <HStack mb={2}>
                <Text fontWeight="semibold" fontSize="sm">
                  Mandatory Documents
                </Text>
                <Badge colorScheme="red" fontSize="xs">
                  Required
                </Badge>
              </HStack>
              <List spacing={2}>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="red.500" />
                  Signed Application
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="red.500" />
                  Autoplus
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="red.500" />
                  MVR
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="red.500" />
                  Confirm New Business Abeyance Set
                </ListItem>
              </List>
            </Box>

            <Box>
              <Text fontWeight="semibold" fontSize="sm" mb={2}>
                Additional Documents (if applicable)
              </Text>
              <List spacing={2}>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="gray.400" />
                  Vehicle Ownerships
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="gray.400" />
                  Void Cheque
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="gray.400" />
                  Lienholder/Lessor Information
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="gray.400" />
                  Bill of Sale
                </ListItem>
                <ListItem>
                  <ListIcon as={CheckCircleIcon} color="gray.400" />
                  DL Abstract/Drivers Training Certificate
                </ListItem>
              </List>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCancel}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirm}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default DocumentComplianceSection;
